import React, { useMemo } from "react";
import { Circles } from "react-loader-spinner";
import { useNavigate } from "react-router-dom";
import { formatDate } from "../utils/patientUtils";

interface DiagnoseTabProps {
  examinationData: any[];
  loading: boolean;
  theme?: "light" | "dark";
}

const DiagnoseTab: React.FC<DiagnoseTabProps> = ({
  examinationData,
  loading,
  theme = "light",
}) => {
  const navigate = useNavigate();

  const hasBridge = (b: any) =>
    b &&
    b.bridgeType &&
    (b.lefttooth != null || b.righttooth != null || b.bridgeType);
  const formatBridge = (b: any, toothNo: number) => {
    if (!b || !b.bridgeType) return "-";
    const type =
      b.bridgeType === "conventional-fixed"
        ? "Conventional Fixed"
        : b.bridgeType === "resin-bonded"
          ? "Resin Bonded"
          : b.bridgeType === "cantilever"
            ? "Cantilever"
            : b.bridgeType;
    const parts = [type];
    if (b.lefttooth != null) parts.push(String(b.lefttooth));
    parts.push(String(toothNo));
    if (b.righttooth != null) parts.push(String(b.righttooth));
    return parts.join("–");
  };

  const allDiagnoses = useMemo(() => {
    const rows: any[] = [];
    examinationData.forEach((item: any) => {
      const date = formatDate(item?.updatedAt);
      const treatmentId = item?._id || "";

      (Array.isArray(item?.diagnose) ? item.diagnose : []).forEach((d: any) => {
        rows.push({
          treatmentId,
          date,
          type: "standard",
          toothNo: d?.toothNo ?? "-",
          diagnose: d?.diagnose ?? "-",
          surface: d?.surface ? String(d.surface) : "-",
          note: d?.note ?? "-",
          condition: d?.condition ?? "-",
          rootDiagnoses: "-",
          rootVariantDiagnoses: [],
          bridgeLabel: "-",
          priority: d?.priority,
          estimatetime: d?.estimatetime ?? "-",
        });
      });

      const advanced = Array.isArray(item?.advancedDiagnose)
        ? item.advancedDiagnose.filter((adv: any) => {
            if (!adv) return false;
            const n = adv.toothNo;
            const hasToothNo =
              n !== undefined &&
              n !== null &&
              (typeof n === "number" ||
                (typeof n === "string" &&
                  String(n).trim() !== "" &&
                  !isNaN(Number(n))));
            const hasOther =
              (Array.isArray(adv.surface) && adv.surface.length > 0) ||
              adv.condition ||
              adv.rootDiagnoses ||
              (adv.rootVariantDiagnoses &&
                adv.rootVariantDiagnoses.length > 0) ||
              hasBridge(adv.bridge) ||
              adv.note;
            return hasToothNo || hasOther;
          })
        : [];

      advanced.forEach((adv: any) => {
        const toothNo = adv?.toothNo ?? "-";
        if (
          adv.surface &&
          Array.isArray(adv.surface) &&
          adv.surface.length > 0
        ) {
          adv.surface.forEach((surface: any) => {
            rows.push({
              treatmentId,
              date: formatDate(item?.updatedAt),
              type: "advanced",
              toothNo,
              diagnose: surface.diagnose || "-",
              surface: surface.sid || "-",
              note: adv.note || "-",
              condition: adv.condition || "-",
              rootDiagnoses: adv.rootDiagnoses || "-",
              rootVariantDiagnoses: adv.rootVariantDiagnoses || [],
              bridge: adv.bridge,
              bridgeLabel: formatBridge(adv.bridge, Number(adv?.toothNo) || 0),
              priority: undefined,
              estimatetime: "-",
            });
          });
        } else {
          const hasOther =
            adv.condition ||
            adv.note ||
            adv.rootDiagnoses ||
            (adv.rootVariantDiagnoses && adv.rootVariantDiagnoses.length > 0) ||
            hasBridge(adv.bridge);
          if (hasOther) {
            rows.push({
              treatmentId,
              date: formatDate(item?.updatedAt),
              type: "advanced",
              toothNo,
              diagnose: adv.condition
                ? adv.condition
                : hasBridge(adv.bridge)
                  ? adv.bridge?.bridgeType || "Bridge"
                  : "-",
              surface: "-",
              note: adv.note || "-",
              condition: adv.condition || "-",
              rootDiagnoses: adv.rootDiagnoses || "-",
              rootVariantDiagnoses: adv.rootVariantDiagnoses || [],
              bridge: adv.bridge,
              bridgeLabel: formatBridge(adv.bridge, Number(adv?.toothNo) || 0),
              priority: undefined,
              estimatetime: "-",
            });
          }
        }
      });
    });
    return rows;
  }, [examinationData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Circles height="30" width="30" color="#6366f1" ariaLabel="loading" />
      </div>
    );
  }

  const isDark = theme === "dark";

  return (
    <div
      className={`rounded-[8px] overflow-hidden border ${
        isDark
          ? "bg-slate-800 border-slate-600 shadow-slate-900/50"
          : "bg-white shadow-lg shadow-slate-200/50 border-slate-100"
      }`}
    >
      <div className="p-2 sm:p-3 md:p-4">
        {allDiagnoses.length > 0 ? (
          <>
            {/* Mobile Card View - same as DiagnoseTable */}
            <div className="block md:hidden space-y-3">
              {allDiagnoses.map((item: any, i: number) => (
                <div
                  key={i}
                  className={`rounded-lg p-3 shadow-sm border ${
                    item.type === "advanced"
                      ? isDark
                        ? "border-blue-700 bg-blue-900/20"
                        : "border-blue-200 bg-blue-50/30"
                      : isDark
                        ? "border-slate-600 bg-slate-700/50"
                        : "border-slate-200 bg-white"
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      {item.type === "advanced" ? (
                        <span
                          className={`px-2 py-0.5 rounded-md text-xs font-medium ${
                            isDark
                              ? "bg-blue-900/50 text-blue-300"
                              : "bg-blue-100 text-blue-700"
                          }`}
                        >
                          Advanced
                        </span>
                      ) : (
                        <span
                          className={`px-2 py-0.5 rounded-md text-xs font-medium ${
                            isDark
                              ? "bg-slate-600 text-slate-300"
                              : "bg-slate-100 text-slate-600"
                          }`}
                        >
                          Standard
                        </span>
                      )}
                      {item.priority && (
                        <span
                          className={`px-2 py-0.5 rounded-md text-xs font-medium ${
                            item.priority === "heigh"
                              ? "bg-red-100 text-red-700"
                              : isDark
                                ? "bg-slate-600 text-slate-300"
                                : "bg-slate-100 text-slate-600"
                          }`}
                        >
                          {item.priority === "heigh" ? "High" : "Normal"}
                        </span>
                      )}
                      {item.date && (
                        <span
                          className={`text-xs ${
                            isDark ? "text-slate-400" : "text-slate-500"
                          }`}
                        >
                          {item.date}
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => {
                        if (item.treatmentId) {
                          navigate(
                            `/client/treatment-diagnose/add-treatment/diagnose/${item.treatmentId}`
                          );
                        }
                      }}
                      className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
                        isDark
                          ? "bg-indigo-900/50 text-indigo-300 hover:bg-indigo-800/50"
                          : "bg-orange-100 text-orange-600 hover:bg-orange-200"
                      }`}
                    >
                      View
                    </button>
                  </div>
                  <div
                    className={`space-y-1.5 text-xs ${
                      isDark ? "text-slate-300" : ""
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className={`font-semibold w-20 ${
                          isDark ? "text-slate-400" : "text-slate-500"
                        }`}
                      >
                        Diagnose:
                      </span>
                      <span
                        className={
                          isDark ? "text-slate-200 font-medium" : "text-slate-800 font-medium"
                        }
                      >
                        {item.diagnose || "-"}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className={`font-semibold w-20 ${
                          isDark ? "text-slate-400" : "text-slate-500"
                        }`}
                      >
                        Tooth No:
                      </span>
                      <span
                        className={isDark ? "text-slate-300" : "text-slate-600"}
                      >
                        {item.toothNo || "-"}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className={`font-semibold w-20 ${
                          isDark ? "text-slate-400" : "text-slate-500"
                        }`}
                      >
                        Surface:
                      </span>
                      <span
                        className={isDark ? "text-slate-300" : "text-slate-600"}
                      >
                        {item.type === "advanced" &&
                        item.rootVariantDiagnoses &&
                        Array.isArray(item.rootVariantDiagnoses) &&
                        item.rootVariantDiagnoses.length > 0
                          ? item.rootVariantDiagnoses
                              .map(
                                (rv: any) =>
                                  `Root ${rv.rootNo}: ${rv.diagnose}`
                              )
                              .join(", ")
                          : item.surface || "-"}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className={`font-semibold w-20 ${
                          isDark ? "text-slate-400" : "text-slate-500"
                        }`}
                      >
                        Condition:
                      </span>
                      {item.condition && item.condition !== "-" ? (
                        <span
                          className={`px-2 py-0.5 rounded-md text-xs font-medium ${
                            item.condition === "missing"
                              ? "bg-red-100 text-red-700"
                              : item.condition === "present"
                                ? "bg-green-100 text-green-700"
                                : isDark
                                  ? "bg-slate-600 text-slate-300"
                                  : "bg-slate-100 text-slate-600"
                          }`}
                        >
                          {item.condition}
                        </span>
                      ) : (
                        <span
                          className={isDark ? "text-slate-400" : "text-slate-600"}
                        >
                          -
                        </span>
                      )}
                    </div>
                    {item.rootDiagnoses && item.rootDiagnoses !== "-" && (
                      <div className="flex items-center gap-2">
                        <span
                          className={`font-semibold w-20 ${
                            isDark ? "text-slate-400" : "text-slate-500"
                          }`}
                        >
                          Root:
                        </span>
                        <span className="px-2 py-0.5 rounded-md text-xs font-medium bg-purple-100 text-purple-700">
                          {item.rootDiagnoses}
                        </span>
                      </div>
                    )}
                    {item.type === "advanced" &&
                      item.bridgeLabel &&
                      item.bridgeLabel !== "-" && (
                        <div className="flex items-center gap-2">
                          <span
                            className={`font-semibold w-20 ${
                              isDark ? "text-slate-400" : "text-slate-500"
                            }`}
                          >
                            Bridge:
                          </span>
                          <span className="px-2 py-0.5 rounded-md text-xs font-medium bg-cyan-100 text-cyan-700">
                            {item.bridgeLabel}
                          </span>
                        </div>
                      )}
                    {item.note && item.note !== "-" && (
                      <div className="flex items-start gap-2">
                        <span
                          className={`font-semibold w-20 ${
                            isDark ? "text-slate-400" : "text-slate-500"
                          }`}
                        >
                          Note:
                        </span>
                        <span
                          className={
                            isDark ? "text-slate-300 flex-1" : "text-slate-600 flex-1"
                          }
                        >
                          {item.note}
                        </span>
                      </div>
                    )}
                    {item.estimatetime && item.estimatetime !== "-" && (
                      <div className="flex items-center gap-2">
                        <span
                          className={`font-semibold w-20 ${
                            isDark ? "text-slate-400" : "text-slate-500"
                          }`}
                        >
                          Est. Time:
                        </span>
                        <span
                          className={isDark ? "text-slate-300" : "text-slate-600"}
                        >
                          {item.estimatetime}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table View - same columns as DiagnoseTable */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead
                  className={
                    isDark ? "bg-slate-700/50 border-b border-slate-600" : "bg-slate-50 border-b border-slate-100"
                  }
                >
                  <tr>
                    <th
                      className={`px-3 py-2 text-left text-xs font-semibold uppercase ${
                        isDark ? "text-slate-400" : "text-slate-500"
                      }`}
                    >
                      Date
                    </th>
                    <th
                      className={`px-3 py-2 text-left text-xs font-semibold uppercase ${
                        isDark ? "text-slate-400" : "text-slate-500"
                      }`}
                    >
                      Type
                    </th>
                    <th
                      className={`px-3 py-2 text-left text-xs font-semibold uppercase ${
                        isDark ? "text-slate-400" : "text-slate-500"
                      }`}
                    >
                      Diagnose
                    </th>
                    <th
                      className={`px-3 py-2 text-left text-xs font-semibold uppercase ${
                        isDark ? "text-slate-400" : "text-slate-500"
                      }`}
                    >
                      Tooth No
                    </th>
                    <th
                      className={`px-3 py-2 text-left text-xs font-semibold uppercase ${
                        isDark ? "text-slate-400" : "text-slate-500"
                      }`}
                    >
                      Surface
                    </th>
                    <th
                      className={`px-3 py-2 text-left text-xs font-semibold uppercase ${
                        isDark ? "text-slate-400" : "text-slate-500"
                      }`}
                    >
                      Condition
                    </th>
                    <th
                      className={`px-3 py-2 text-left text-xs font-semibold uppercase ${
                        isDark ? "text-slate-400" : "text-slate-500"
                      }`}
                    >
                      Root
                    </th>
                    <th
                      className={`px-3 py-2 text-left text-xs font-semibold uppercase ${
                        isDark ? "text-slate-400" : "text-slate-500"
                      }`}
                    >
                      Bridge
                    </th>
                    <th
                      className={`px-3 py-2 text-left text-xs font-semibold uppercase ${
                        isDark ? "text-slate-400" : "text-slate-500"
                      }`}
                    >
                      Note
                    </th>
                    <th
                      className={`px-3 py-2 text-left text-xs font-semibold uppercase ${
                        isDark ? "text-slate-400" : "text-slate-500"
                      }`}
                    >
                      Priority
                    </th>
                    <th
                      className={`px-3 py-2 text-left text-xs font-semibold uppercase ${
                        isDark ? "text-slate-400" : "text-slate-500"
                      }`}
                    >
                      Est. Time
                    </th>
                    <th
                      className={`px-3 py-2 text-left text-xs font-semibold uppercase w-20 ${
                        isDark ? "text-slate-400" : "text-slate-500"
                      }`}
                    >
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody
                  className={
                    isDark ? "divide-y divide-slate-600" : "divide-y divide-slate-100"
                  }
                >
                  {allDiagnoses.map((item: any, i: number) => (
                    <tr
                      key={i}
                      className={`transition-colors ${
                        item.type === "advanced"
                          ? isDark
                            ? "bg-blue-900/20 hover:bg-blue-900/30"
                            : "bg-blue-50/30 hover:bg-blue-50/50"
                          : isDark
                            ? "hover:bg-slate-700/30"
                            : "hover:bg-slate-50"
                      }`}
                    >
                      <td
                        className={`px-3 py-2 text-xs ${
                          isDark ? "text-slate-400" : "text-slate-600"
                        }`}
                      >
                        {item.date || "-"}
                      </td>
                      <td className="px-3 py-2 text-xs font-medium">
                        {item.type === "advanced" ? (
                          <span
                            className={`px-2 py-0.5 rounded-md text-xs font-medium ${
                              isDark
                                ? "bg-blue-900/50 text-blue-300"
                                : "bg-blue-100 text-blue-700"
                            }`}
                          >
                            Advanced
                          </span>
                        ) : (
                          <span
                            className={`px-2 py-0.5 rounded-md text-xs font-medium ${
                              isDark
                                ? "bg-slate-600 text-slate-300"
                                : "bg-slate-100 text-slate-600"
                            }`}
                          >
                            Standard
                          </span>
                        )}
                      </td>
                      <td
                        className={`px-3 py-2 text-xs font-medium ${
                          isDark ? "text-slate-200" : "text-slate-800"
                        }`}
                      >
                        {item.diagnose || "-"}
                      </td>
                      <td
                        className={`px-3 py-2 text-xs ${
                          isDark ? "text-slate-400" : "text-slate-600"
                        }`}
                      >
                        {item.toothNo || "-"}
                      </td>
                      <td
                        className={`px-3 py-2 text-xs ${
                          isDark ? "text-slate-400" : "text-slate-600"
                        }`}
                      >
                        {item.type === "advanced" &&
                        item.rootVariantDiagnoses &&
                        Array.isArray(item.rootVariantDiagnoses) &&
                        item.rootVariantDiagnoses.length > 0
                          ? item.rootVariantDiagnoses
                              .map(
                                (rv: any) =>
                                  `Root ${rv.rootNo}: ${rv.diagnose}`
                              )
                              .join(", ")
                          : item.surface || "-"}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {item.condition && item.condition !== "-" ? (
                          <span
                            className={`px-2 py-0.5 rounded-md text-xs font-medium ${
                              item.condition === "missing"
                                ? "bg-red-100 text-red-700"
                                : item.condition === "present"
                                  ? "bg-green-100 text-green-700"
                                  : isDark
                                    ? "bg-slate-600 text-slate-300"
                                    : "bg-slate-100 text-slate-600"
                            }`}
                          >
                            {item.condition}
                          </span>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {item.rootDiagnoses && item.rootDiagnoses !== "-" ? (
                          <span className="px-2 py-0.5 rounded-md text-xs font-medium bg-purple-100 text-purple-700">
                            {item.rootDiagnoses}
                          </span>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {item.type === "advanced" &&
                        item.bridgeLabel &&
                        item.bridgeLabel !== "-" ? (
                          <span className="px-2 py-0.5 rounded-md text-xs font-medium bg-cyan-100 text-cyan-700">
                            {item.bridgeLabel}
                          </span>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td
                        className={`px-3 py-2 text-xs max-w-xs truncate ${
                          isDark ? "text-slate-400" : "text-slate-600"
                        }`}
                        title={item.note}
                      >
                        {item.note || "-"}
                      </td>
                      <td className="px-3 py-2">
                        {item.priority ? (
                          <span
                            className={`px-2 py-0.5 rounded-md text-xs font-medium ${
                              item.priority === "heigh"
                                ? "bg-red-100 text-red-700"
                                : isDark
                                  ? "bg-slate-600 text-slate-300"
                                  : "bg-slate-100 text-slate-600"
                            }`}
                          >
                            {item.priority === "heigh" ? "High" : "Normal"}
                          </span>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td
                        className={`px-3 py-2 text-xs ${
                          isDark ? "text-slate-400" : "text-slate-600"
                        }`}
                      >
                        {item.estimatetime || "-"}
                      </td>
                      <td className="px-3 py-2">
                        <button
                          onClick={() => {
                            if (item.treatmentId) {
                              navigate(
                                `/client/treatment-diagnose/add-treatment/diagnose/${item.treatmentId}`
                              );
                            }
                          }}
                          className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
                            isDark
                              ? "bg-indigo-900/50 text-indigo-300 hover:bg-indigo-800/50"
                              : "bg-orange-100 text-orange-600 hover:bg-orange-200"
                          }`}
                          title="View / Edit Diagnose"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div
            className={`text-center py-4 ${
              isDark ? "text-slate-400" : "text-slate-400"
            }`}
          >
            <p className="text-xs">No diagnoses found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DiagnoseTab;
